import { useState } from 'react';
import { MessageCircle, Mail, MapPin, Send, Info, BadgeCheck, Building2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SectionHeading from '../components/SectionHeading';
import { FadeUp } from '../components/Reveal';
import { site, waUrl, DEFAULT_WA_MESSAGE } from '../data/site';

export default function Contato() {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const composed = [
    `Olá, Eng. Amilcar! Meu nome é ${name || '—'}.`,
    'Acessei o site do Grupo Majo e gostaria de falar sobre um serviço de engenharia.',
    '',
    `*Mensagem:* ${message || '—'}`,
  ].join('\n');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) {
      setError('Preencha seu nome e sua mensagem para continuar.');
      return;
    }
    setError('');
    window.open(waUrl(composed), '_blank', 'noopener,noreferrer');
  };

  const CONTACT_CARDS = [
    {
      icon: MessageCircle,
      title: 'WhatsApp Comercial',
      value: site.contact.phoneDisplay,
      note: 'Resposta direta com o engenheiro responsável',
      href: waUrl(DEFAULT_WA_MESSAGE),
      testId: 'contact-whatsapp-card',
      cta: 'Abrir conversa',
    },
    {
      icon: Mail,
      title: 'E-mail Técnico',
      value: site.contact.email,
      note: 'Envio de plantas, documentos e escopos',
      href: `mailto:${site.contact.email}`,
      testId: 'contact-email-card',
      cta: 'Enviar e-mail',
    },
    {
      icon: MapPin,
      title: 'Área de Cobertura',
      value: 'Todo o Brasil',
      note: site.contact.coverage,
      href: null,
      testId: 'contact-coverage-card',
      cta: null,
    },
  ];

  return (
    <>
      <PageHeader
        index="08"
        eyebrow="Fale Conosco"
        title="Vamos tirar sua obra do papel — com papel assinado"
        description="WhatsApp, e-mail ou mensagem formatada: escolha o canal e receba atendimento técnico direto com o engenheiro responsável."
      />

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28" data-testid="contact-section">
        <div className="grid gap-6 md:grid-cols-3">
          {CONTACT_CARDS.map((c, i) => {
            const Icon = c.icon;
            const Wrapper = c.href ? 'a' : 'div';
            return (
              <FadeUp key={c.title} delay={i * 0.08}>
                <Wrapper
                  {...(c.href ? { href: c.href, target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className="card-hover flex h-full flex-col rounded-2xl surface p-8"
                  data-testid={c.testId}
                >
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-navy text-gold-light dark:bg-white/10">
                    <Icon size={22} strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-navy dark:text-white">{c.title}</h3>
                  <p className="mt-2 font-semibold text-azure dark:text-gold-light">{c.value}</p>
                  <p className="mt-2 flex-1 text-sm text-muted-c">{c.note}</p>
                  {c.cta && (
                    <span className="mt-5 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-gold">
                      {c.cta}
                    </span>
                  )}
                </Wrapper>
              </FadeUp>
            );
          })}
        </div>

        <div className="mt-16 grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
          <FadeUp>
            <SectionHeading
              eyebrow="Mensagem Rápida"
              title="Envie sua demanda agora"
              description="Preencha e clique: abrimos o WhatsApp com sua mensagem pronta. Nenhum dado é armazenado neste site."
            />
            <form onSubmit={handleSubmit} className="mt-8 space-y-5 rounded-3xl surface p-7 md:p-9" data-testid="contact-form" noValidate>
              <div>
                <label htmlFor="contactName" className="mb-2 block font-display text-sm font-bold text-navy dark:text-white">
                  Seu nome <span className="text-gold">*</span>
                </label>
                <input
                  id="contactName"
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setError(''); }}
                  placeholder="Ex.: Marcelo, Gerente de Facilities"
                  data-testid="contact-name-input"
                  className="w-full rounded-xl border border-line bg-[var(--bg)] px-4 py-3.5 text-sm outline-none transition-colors focus:border-gold"
                />
              </div>
              <div>
                <label htmlFor="contactMessage" className="mb-2 block font-display text-sm font-bold text-navy dark:text-white">
                  Mensagem <span className="text-gold">*</span>
                </label>
                <textarea
                  id="contactMessage"
                  rows="4"
                  value={message}
                  onChange={(e) => { setMessage(e.target.value); setError(''); }}
                  placeholder="Conte brevemente o que você precisa: laudo, ART, reforma, regularização..."
                  data-testid="contact-message-textarea"
                  className="w-full resize-none rounded-xl border border-line bg-[var(--bg)] px-4 py-3.5 text-sm outline-none transition-colors focus:border-gold"
                />
              </div>
              {error && (
                <p className="rounded-xl border border-red-300/60 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-600 dark:text-red-400" data-testid="contact-form-error">
                  {error}
                </p>
              )}
              <button type="submit" className="btn-gold w-full" data-testid="contact-form-submit">
                <Send size={16} /> Enviar pelo WhatsApp
              </button>
              <p className="flex items-start gap-2 text-xs leading-relaxed text-muted-c" data-testid="contact-privacy-note">
                <Info size={14} className="mt-0.5 shrink-0 text-gold" />
                Atendimento em todo o território nacional · Nenhuma informação é salva neste site.
              </p>
            </form>
          </FadeUp>

          <FadeUp delay={0.15}>
            <div className="overflow-hidden rounded-3xl border border-gold/30 bg-navy p-8 text-white md:p-10" data-testid="contact-institutional-card">
              <p className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.22em] text-gold-light">
                <Building2 size={15} /> Dados Institucionais
              </p>
              <h3 className="mt-5 font-display text-2xl font-extrabold">Grupo Majo</h3>
              <p className="text-sm text-white/60">Majo Processos e Intermediações LTDA</p>
              <dl className="mt-7 space-y-5 text-sm">
                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/45">CNPJ</dt>
                  <dd className="mt-1 font-semibold">{site.contact.cnpj}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/45">Responsável Técnico</dt>
                  <dd className="mt-1 font-semibold">{site.contact.technicalResponsible}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/45">Registro Profissional</dt>
                  <dd className="mt-1 flex items-center gap-2 font-semibold">
                    <BadgeCheck size={16} className="text-gold" /> {site.contact.crea} — registro e autorizações ativas
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/45">Normas Atendidas</dt>
                  <dd className="mt-1 leading-relaxed text-white/70">
                    ABNT NBR 5410, 5674, 13752, 16280 e 16747 · NR-10, NR-18 e NR-35 · ART emitida para 100% dos serviços
                  </dd>
                </div>
              </dl>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
